import * as React from "react";

import { v4 as uuidv4 } from "uuid";

import type { DocumentType } from "@needl-ai/common/upload/hooks/useInitUploadComplete";

export type AttachmentStatus =
  | "COMPLETED"
  | "PROCESSING_ERROR"
  | "PROCESSING"
  | "UPLOADED"
  | "UPLOADING_ERROR"
  | "UPLOADING";

type DocInfoType = {
  source: string;
  bucket: string;
  key: string;
  metadata: {
    fileName: string;
    mimeType: string;
    guid: string;
  };
};

export type FileItem = DocumentType & {
  progress: number;
  status: AttachmentStatus;
  info?: DocInfoType;
};

export type FileState = {
  attachment_feed_id: string | null;
  attachments: FileItem[];
};

export type FileAction =
  | {
      type: "ADD_ATTACHMENTS";
      payload: File[];
    }
  | {
      type: "ADD_ATTACHMENTS_WITH_IDS";
      payload: FileItem[];
    }
  | {
      type: "REMOVE_ATTACHMENTS";
      payload: string[];
    }
  | {
      type: "UPDATE_PROGRESS";
      payload: {
        fileId: string;
        progress: number;
      };
    }
  | {
      type: "UPDATE_STATUS_OF_ATTACHMENTS";
      payload: {
        fileIds: string[];
        status: AttachmentStatus;
      };
    }
  | {
      type: "UPDATE_STATUS_OF_ATTACHMENT";
      payload: {
        fileId: string;
        status: AttachmentStatus;
      };
    }
  | {
      type: "UPDATE_UPLOAD_STATUS";
      payload: {
        fileId: string;
        status: AttachmentStatus;
        info?: DocInfoType;
      };
    }
  | {
      type: "SET_ATTACHMENT_FEED_ID";
      payload: string;
    }
  | {
      type: "CLEAR_ATTACHMENTS";
    }
  | {
      type: "CLEAR_ATTACHMENTS_ONLY";
    };

const initialState: FileState = {
  attachment_feed_id: null,
  attachments: [],
};

const fileReducer = (state: FileState, action: FileAction): FileState => {
  switch (action.type) {
    case "ADD_ATTACHMENTS": {
      const files = action.payload;
      const validFiles: FileItem[] = [];

      files.forEach((file) => {
        const documentFile: FileItem = {
          id: uuidv4(),
          name: file.name,
          mime_type: file.type,
          file: file,
          progress: 0,
          status: "UPLOADING",
        };

        validFiles.push(documentFile);
      });

      return {
        ...state,
        attachments: [...state.attachments, ...validFiles],
      };
    }

    case "ADD_ATTACHMENTS_WITH_IDS": {
      const fileItems = action.payload;
      return {
        ...state,
        attachments: [...state.attachments, ...fileItems],
      };
    }

    case "REMOVE_ATTACHMENTS": {
      const fileIdsToRemove = action.payload;
      return {
        ...state,
        attachments: state.attachments.filter(
          (file) => !fileIdsToRemove.includes(file.id)
        ),
      };
    }

    case "UPDATE_PROGRESS": {
      const { fileId, progress } = action.payload;
      return {
        ...state,
        attachments: state.attachments.map((file) =>
          file.id === fileId ? { ...file, progress } : file
        ),
      };
    }

    case "UPDATE_STATUS_OF_ATTACHMENTS": {
      const { fileIds, status } = action.payload;
      return {
        ...state,
        attachments: state.attachments.map((file) =>
          fileIds.includes(file.id) ? { ...file, status } : file
        ),
      };
    }

    case "UPDATE_STATUS_OF_ATTACHMENT": {
      const { fileId, status } = action.payload;
      return {
        ...state,
        attachments: state.attachments.map((file) =>
          file.id === fileId ? { ...file, status } : file
        ),
      };
    }

    case "UPDATE_UPLOAD_STATUS": {
      const { fileId, status, info } = action.payload;
      return {
        ...state,
        attachments: state.attachments.map((file) =>
          file.id === fileId ? { ...file, status, info } : file
        ),
      };
    }

    case "SET_ATTACHMENT_FEED_ID": {
      return {
        ...state,
        attachment_feed_id: action.payload,
      };
    }

    case "CLEAR_ATTACHMENTS": {
      return {
        ...state,
        attachment_feed_id: null,
        attachments: [],
      };
    }

    case "CLEAR_ATTACHMENTS_ONLY": {
      return {
        ...state,
        attachments: [],
      };
    }

    default:
      return state;
  }
};

type FileContextType = {
  state: FileState;
  dispatch: React.Dispatch<FileAction>;
};

const FileUploadContext = React.createContext<FileContextType | undefined>(
  undefined
);

export const FileUploadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(fileReducer, initialState);

  return (
    <FileUploadContext.Provider value={{ state, dispatch }}>
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUploadContext = (): FileContextType => {
  const context = React.useContext(FileUploadContext);
  if (!context) {
    throw new Error(
      "useFileUploadContext must be used within a FileUploadProvider"
    );
  }
  return context;
};
